// Every server resource the UI reads, as TanStack Query options. Route loaders prefetch them so pages render
// complete on first paint (no loading → content jumps); components read them with useSuspenseQuery.
import { QueryClient, queryOptions, useMutation, useQuery, useQueryClient, useSuspenseQuery, type EnsureQueryDataOptions, type QueryKey } from '@tanstack/react-query'
import { api, ApiError } from '../lib/api'
import type { Application } from '../modules/applications/data'
import type { PortalModule } from '../modules/registry'
import type { PortalTenant } from '../modules/tenants'
import type { Survey, SurveyAnalysis, SurveyCatalog, SurveyReport } from '../modules/surveys/data'
import type {
  AdminTraining, AssessmentState, CertificateRecord, Leaderboard, MyCertificate, RankedEntry, RewardRecipient, Training, TrainingProfile,
} from '../modules/training/types'
import type { TeamMember } from './team'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (count, error) => !(error instanceof ApiError && error.status > 0 && error.status < 500) && count < 2,
    },
  },
})

export interface Me {
  user: { id: string; name: string; email: string; department: string; tenantId: string; isAdmin: boolean }
  tenant: PortalTenant
  /** Visible modules assigned to my tenant, in Home order. */
  modules: PortalModule[]
  serverTime: string
}

export interface PortalSettings { contactItEmail: string }

export interface AuthConfig {
  microsoftEnabled: boolean
  devLoginEnabled: boolean
  devUsers: { email: string; name: string; department: string; isAdmin: boolean }[]
}

export interface ChatSettings {
  embeddingModel: string
  ragEnabled: boolean
  enabled: boolean
  model: string
  surveyModel: string
  sameModelForAll: boolean
  keyConfigured: boolean
}

export const q = {
  portalConfig: queryOptions({ queryKey: ['portal-config'], queryFn: () => api.get<PortalSettings>('/portal/config'), staleTime: 60_000 }),
  me: queryOptions({ queryKey: ['me'], queryFn: () => api.get<Me>('/me'), staleTime: 60_000 }),
  authConfig: queryOptions({ queryKey: ['auth-config'], queryFn: () => api.get<AuthConfig>('/auth/config'), staleTime: Infinity }),

  trainings: queryOptions({ queryKey: ['training', 'list'], queryFn: () => api.get<Training[]>('/training/trainings') }),
  training: (slug: string) => queryOptions({ queryKey: ['training', 'detail', slug], queryFn: () => api.get<Training>(`/training/trainings/${slug}`) }),
  assessment: (slug: string) =>
    queryOptions({ queryKey: ['training', 'assessment', slug], queryFn: () => api.get<AssessmentState>(`/training/trainings/${slug}/assessment`), staleTime: 0 }),
  leaderboard: (slug: string) => queryOptions({ queryKey: ['training', 'leaderboard', slug], queryFn: () => api.get<Leaderboard>(`/training/trainings/${slug}/leaderboard`) }),
  myCertificate: (slug: string) => queryOptions({ queryKey: ['training', 'my-certificate', slug], queryFn: () => api.get<MyCertificate>(`/training/trainings/${slug}/certificate`) }),
  certificate: (id: string) => queryOptions({ queryKey: ['training', 'certificate', id], queryFn: () => api.get<CertificateRecord>(`/training/certificates/${id}`) }),
  profile: queryOptions({ queryKey: ['training', 'profile'], queryFn: () => api.get<TrainingProfile>('/training/profile') }),

  applications: queryOptions({ queryKey: ['applications', 'list'], queryFn: () => api.get<Application[]>('/applications') }),
  surveys: queryOptions({ queryKey: ['surveys', 'list'], queryFn: () => api.get<Survey[]>('/surveys') }),
  survey: (id: string) => queryOptions({ queryKey: ['surveys', 'detail', id], queryFn: () => api.get<Survey>(`/surveys/${id}`), staleTime: 0 }),
  team: queryOptions({ queryKey: ['team', 'list'], queryFn: () => api.get<{ categories: string[]; members: TeamMember[] }>('/team') }),

  manage: {
    settings: queryOptions({ queryKey: ['manage', 'settings'], queryFn: () => api.get<PortalSettings>('/manage/settings'), staleTime: 0 }),
    trainings: queryOptions({ queryKey: ['manage', 'trainings'], queryFn: () => api.get<AdminTraining[]>('/manage/trainings') }),
    training: (slug: string) => queryOptions({ queryKey: ['manage', 'training', slug], queryFn: () => api.get<AdminTraining>(`/manage/trainings/${slug}`), staleTime: 0 }),
    results: (slug: string) => queryOptions({ queryKey: ['manage', 'results', slug], queryFn: () => api.get<{ rows: RankedEntry[]; recipients: RewardRecipient[] }>(`/manage/trainings/${slug}/results`), staleTime: 0 }),
    applications: queryOptions({ queryKey: ['manage', 'applications'], queryFn: () => api.get<Application[]>('/manage/applications') }),
    surveys: queryOptions({ queryKey: ['manage', 'surveys'], queryFn: () => api.get<SurveyCatalog>('/manage/surveys') }),
    survey: (id: string) => queryOptions({ queryKey: ['manage', 'survey', id], queryFn: () => api.get<Survey>(`/manage/surveys/${id}`), staleTime: 0 }),
    surveyResults: (id: string, search = '') => queryOptions({ queryKey: ['manage', 'survey-results', id, search], queryFn: () => api.get<SurveyReport>(`/manage/surveys/${id}/results${search ? `?${search}` : ''}`), staleTime: 0 }),
    surveyAnalysis: (id: string, search = '') => queryOptions({ queryKey: ['manage', 'survey-analysis', id, search], queryFn: () => api.get<SurveyAnalysis>(`/manage/surveys/${id}/analysis${search ? `?${search}` : ''}`), staleTime: 60_000 }),
    team: queryOptions({ queryKey: ['manage', 'team'], queryFn: () => api.get<TeamMember[]>('/manage/team') }),
    modules: queryOptions({ queryKey: ['manage', 'modules'], queryFn: () => api.get<PortalModule[]>('/manage/modules') }),
    tenants: queryOptions({ queryKey: ['manage', 'tenants'], queryFn: () => api.get<PortalTenant[]>('/manage/tenants') }),
    chat: queryOptions({ queryKey: ['manage', 'chat'], queryFn: () => api.get<ChatSettings>('/manage/chat'), staleTime: 0 }),
  },
}

/** Prefetch for a loader: resolves from cache when present (refreshing stale data in the background). */
export function ensure<TData, TKey extends QueryKey>(options: EnsureQueryDataOptions<TData, Error, TData, TKey>) {
  return queryClient.ensureQueryData({ ...options, revalidateIfStale: true })
}

/**
 * A mutation that refreshes related data afterwards. `invalidate` lists query-key prefixes;
 * `onData` can write the response straight into the cache so the UI updates without a refetch.
 */
export function useApiMutation<TVars, TData>(
  fn: (vars: TVars) => Promise<TData>,
  { invalidate = [], onData }: { invalidate?: QueryKey[]; onData?: (data: TData, vars: TVars) => void } = {},
) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: async (data, vars) => {
      onData?.(data, vars)
      await Promise.all(invalidate.map((queryKey) => client.invalidateQueries({ queryKey })))
    },
  })
}

export const upload = async (file: Blob, purpose: 'cover' | 'team-photo' | 'module-image' | 'application-image', filename = 'upload.jpg') => {
  const form = new FormData()
  form.append('purpose', purpose)
  form.append('file', file, filename)
  return api.post<{ id: string; url: string }>('/manage/uploads', form)
}

/** The signed-in user. Always present under the portal shell (its loader fetched it). */
export const useMe = () => useSuspenseQuery(q.me).data

/** Always-fresh prefetch, for pages that must start from the server's current state (assessment, editors). */
export const fresh = queryClient.fetchQuery.bind(queryClient)

/** Public contact details also work before sign-in. */
export const useContactEmail = () => useQuery(q.portalConfig).data?.contactItEmail ?? 'info@ritcco.com'
